import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, MapPin, Users, ChefHat, Star, Calendar, Clock, Loader, AlertCircle, DollarSign, Utensils, Heart, Beer, Building2, User, Globe, Coffee } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useError } from '../contexts/ErrorContext';
import LocationSearch from '../components/LocationSearch';
import LoadingIndicator from '../components/LoadingIndicator';
import FilterSection from '../components/FilterSection';
import { cuisineOptions, priceRangeOptions, dietaryOptions, languageOptions, interestOptions } from '../data/filterOptions';
import BackButton from '../components/BackButton';
import { filterItems, getSearchableName, getVenueFilterableProperties, getEventFilterableProperties, getProfileFilterableProperties } from '../lib/filterUtils';
import AuthError from '../components/AuthError';
import FriendRequestButton from '../components/FriendRequestButton';
import DinnerRecommendationButton from '../components/DinnerRecommendationButton';

const Nearby = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { setError } = useError();
  const [loading, setLoading] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [venues, setVenues] = useState<any[]>([]);
  const [filteredVenues, setFilteredVenues] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [filteredProfiles, setFilteredProfiles] = useState<any[]>([]);
  const [creatingChat, setCreatingChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [animateCards, setAnimateCards] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<{
    address: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [searchRadius, setSearchRadius] = useState(10);

  const [filters, setFilters] = useState({
    languages: [] as string[],
    interests: [] as string[],
    dietaryPreferences: [] as string[]
  });

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchNearbyData();
    }
  }, [selectedLocation, searchRadius]);

  useEffect(() => {
    // Apply filtering whenever search query or filters change
    const allSelectedFilters = [
      ...selectedFilters,
      ...filters.languages,
      ...filters.interests,
      ...filters.dietaryPreferences
    ];
    
    const filteredVenuesResult = filterItems(
      venues,
      searchQuery,
      allSelectedFilters,
      getSearchableName,
      getVenueFilterableProperties
    );
    setFilteredVenues(filteredVenuesResult);

    const filteredEventsResult = filterItems(
      events,
      searchQuery,
      allSelectedFilters,
      getSearchableName,
      getEventFilterableProperties
    );
    setFilteredEvents(filteredEventsResult);

    const filteredProfilesResult = filterItems(
      profiles,
      searchQuery,
      allSelectedFilters,
      getSearchableName,
      getProfileFilterableProperties
    );
    setFilteredProfiles(filteredProfilesResult);
  }, [venues, events, profiles, searchQuery, selectedFilters, filters]);

  useEffect(() => {
    // Trigger card animations after initial load
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    return () => clearTimeout(timer);
  }, [filteredVenues, filteredEvents, filteredProfiles]);

  const initialize = async () => {
    try {
      setLoading(true);
      setLocalError(null);

      // Check authentication status
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      
      if (authError) {
        console.error("Auth error:", authError);
        setIsAuthenticated(false);
        // Don't throw here, we'll handle it gracefully
      } else {
        setIsAuthenticated(!!session);
        setCurrentUser(session?.user || null);
      }

      if (session?.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('location_lat, location_lng, location')
          .eq('id', session.user.id)
          .single();

        if (profileError) {
          console.warn("Error fetching profile:", profileError);
        } else if (profile?.location_lat && profile?.location_lng) {
          setSelectedLocation({
            address: profile.location || '',
            lat: profile.location_lat,
            lng: profile.location_lng
          });
        }
      }
    } catch (err) {
      console.error('Error initializing:', err);
      setLocalError(err instanceof Error ? err.message : 'Failed to initialize');
    } finally {
      setLoading(false);
    }
  };

  const fetchNearbyData = async () => {
    if (!selectedLocation) return;

    try {
      setLoading(true);
      setLocalError(null);

      await Promise.all([
        fetchNearbyVenues(selectedLocation.lat, selectedLocation.lng),
        fetchNearbyEvents(selectedLocation.lat, selectedLocation.lng),
        fetchNearbyProfiles(selectedLocation.lat, selectedLocation.lng)
      ]);
    } catch (err) {
      console.error('Error fetching nearby data:', err);
      setLocalError(err instanceof Error ? err.message : 'Failed to fetch nearby data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNearbyVenues = async (lat: number, lng: number) => {
    try {
      const { data: nearbyVenues, error: venuesError } = await supabase
        .rpc('get_nearby_venues', {
          user_lat: lat,
          user_lng: lng,
          radius_miles: searchRadius
        });

      if (venuesError) throw venuesError;
      
      // Log the venues data to debug
      console.log('Nearby venues:', nearbyVenues);
      
      setVenues(nearbyVenues || []);
      setFilteredVenues(nearbyVenues || []);
    } catch (err) {
      console.error('Error fetching nearby venues:', err);
      throw err;
    }
  };

  const fetchNearbyEvents = async (lat: number, lng: number) => {
    try {
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().split(' ')[0];

      const { data: dinners, error: dinnersError } = await supabase
        .from('dinners')
        .select(`
          *,
          restaurant:restaurants(*)
        `)
        .eq('status', 'open')
        .gt('spots_available', 0)
        .or(`date.gt.${currentDate},and(date.eq.${currentDate},time.gt.${currentTime})`);

      if (dinnersError) throw dinnersError;

      const { data: blindDates, error: blindDatesError } = await supabase
        .from('blind_dates')
        .select(`
          *,
          restaurant:restaurants(*),
          bar:bars(*)
        `)
        .eq('status', 'open')
        .gt('spots_available', 0)
        .or(`date.gt.${currentDate},and(date.eq.${currentDate},time.gt.${currentTime})`);

      if (blindDatesError) throw blindDatesError;

      const { data: drinkingNights, error: drinkingNightsError } = await supabase
        .from('drinking_nights')
        .select(`
          *,
          bar:bars(*)
        `)
        .eq('status', 'open')
        .gt('spots_available', 0)
        .or(`date.gt.${currentDate},and(date.eq.${currentDate},time.gt.${currentTime})`);

      if (drinkingNightsError) throw drinkingNightsError;

      const allEvents = [
        ...(dinners || []).map(d => ({ ...d, type: 'dinner' })),
        ...(blindDates || []).map(b => ({ ...b, type: 'blind_date' })),
        ...(drinkingNights || []).map(d => ({ ...d, type: 'drinking_night' }))
      ].sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateA.getTime() - dateB.getTime();
      });

      setEvents(allEvents);
      setFilteredEvents(allEvents);
    } catch (err) {
      console.error('Error fetching nearby events:', err);
      throw err;
    }
  };

  const fetchNearbyProfiles = async (lat: number, lng: number) => {
    try {
      // Calculate bounding box for the selected location (roughly 50km radius)
      const latRange = 0.45; // Approximately 50km in latitude
      const lngRange = 0.45 / Math.cos(lat * Math.PI / 180); // Adjust for longitude based on latitude

      const { data: nearbyProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .eq('privacy_settings->>profile_visibility', 'public')
        .not('full_name', 'is', null)
        .not('id', 'eq', currentUser?.id)
        .gte('location_lat', lat - latRange)
        .lte('location_lat', lat + latRange)
        .gte('location_lng', lng - lngRange)
        .lte('location_lng', lng + lngRange)
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;
      setProfiles(nearbyProfiles || []);
      setFilteredProfiles(nearbyProfiles || []);
    } catch (err) {
      console.error('Error fetching nearby profiles:', err);
      throw err;
    }
  };

  const handleMessageClick = async (e: React.MouseEvent, profileId: string) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    try {
      setCreatingChat(true);
      setLocalError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      // Create or get chat room
      const { data: chatRoom, error: chatError } = await supabase
        .from('chat_rooms')
        .insert({
          participant1_id: user.id,
          participant2_id: profileId
        })
        .select()
        .single();

      if (chatError) {
        // Check if error is due to unique constraint violation
        if (chatError.code === '23505') {
          // Get existing chat room
          const { data: existingRoom, error: roomError } = await supabase
            .from('chat_rooms')
            .select('id')
            .or(`and(participant1_id.eq.${user.id},participant2_id.eq.${profileId}),and(participant1_id.eq.${profileId},participant2_id.eq.${user.id})`)
            .single();

          if (roomError) throw roomError;
          navigate(`/chat/${existingRoom.id}`);
          return;
        }
        throw chatError;
      }

      navigate(`/chat/${chatRoom.id}`);
    } catch (err) {
      console.error('Error creating chat:', err);
      setLocalError(err instanceof Error ? err.message : 'Failed to create chat');
    } finally {
      setCreatingChat(false);
    }
  };

  const handleLocationSelect = (location: { address: string; lat: number; lng: number }) => {
    setSelectedLocation(location);
  };

  const handleFilterToggle = (filter: string) => {
    setSelectedFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchRadius(parseInt(e.target.value));
  };

  // If we're still checking authentication, show loading
  if (loading && isAuthenticated === null) {
    return <LoadingIndicator message={t('common.loading')} />;
  }

  // If not authenticated, show auth error
  if (isAuthenticated === false) {
    return <AuthError message={t('common.loginRequired')} />;
  }

  if (localError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg flex items-center">
          <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0" />
          <span>{localError}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <BackButton />
      {/* Hero Section */}
      <div className="relative mb-12 rounded-2xl overflow-hidden">
        <div 
          className="h-64 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1511632765486-a01e8e01a772?q=80&w=2000&auto=format&fit=crop')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 transform transition-all duration-500 hover:scale-105">
                {t('nearby.title')}
              </h1>
              <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                {t('nearby.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 group">
            <LocationSearch
              onLocationSelect={handleLocationSelect}
              defaultValue={selectedLocation?.address || ''}
            />
          </div>
          <div className="flex-1 relative group">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('nearby.searchPlaceholder')}
              className="pl-10 w-full p-3 border-2 border-gray-200 rounded-xl transition-all duration-300 focus:border-orange-500 focus:outline-none"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105"
          >
            <Filter className="h-5 w-5" />
            <span>{showFilters ? t('search.hideFilters') : t('search.showFilters')}</span>
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('nearby.searchRadius').replace('{radius}', searchRadius.toString())}
          </label>
          <input
            type="range"
            min="1"
            max="50"
            value={searchRadius}
            onChange={handleRadiusChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1 {t('nearby.kilometer')}</span>
            <span>50 {t('nearby.kilometers')}</span>
          </div>
        </div>

        {showFilters && (
          <div className="grid md:grid-cols-3 gap-6 pt-6 border-t animate-slideDown">
            <FilterSection
              title={t('discover.languages')}
              icon={Globe}
              options={languageOptions}
              selectedFilters={filters.languages}
              onFilterToggle={(language) => {
                const newLanguages = filters.languages.includes(language)
                  ? filters.languages.filter(l => l !== language)
                  : [...filters.languages, language];
                setFilters({ ...filters, languages: newLanguages });
              }}
            />

            <FilterSection
              title={t('discover.interests')}
              icon={Coffee}
              options={interestOptions}
              selectedFilters={filters.interests}
              onFilterToggle={(interest) => {
                const newInterests = filters.interests.includes(interest)
                  ? filters.interests.filter(i => i !== interest)
                  : [...filters.interests, interest];
                setFilters({ ...filters, interests: newInterests });
              }}
            />

            <FilterSection
              title={t('discover.dietary')}
              icon={Utensils}
              options={dietaryOptions}
              selectedFilters={filters.dietaryPreferences}
              onFilterToggle={(pref) => {
                const newPrefs = filters.dietaryPreferences.includes(pref)
                  ? filters.dietaryPreferences.filter(p => p !== pref)
                  : [...filters.dietaryPreferences, pref];
                setFilters({ ...filters, dietaryPreferences: newPrefs });
              }}
            />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-8">
        <button
          className={`px-6 py-3 font-medium text-lg ${
            activeTab === 'venues'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('venues')}
        >
          {t('nearby.venues')}
        </button>
        <button
          className={`px-6 py-3 font-medium text-lg ${
            activeTab === 'events'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('events')}
        >
          {t('nearby.events')}
        </button>
        <button
          className={`px-6 py-3 font-medium text-lg ${
            activeTab === 'people'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('people')}
        >
          {t('nearby.people')}
        </button>
      </div>

      {/* Venues Section */}
      {activeTab === 'venues' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVenues.map((venue, index) => {
            // Get the first available picture from the venue
            const venuePicture = getVenuePicture(venue);
            
            return (
              <div
                key={venue.id}
                className={`bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
                  animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative h-48 overflow-hidden group">
                  {venuePicture ? (
                    <img
                      src={venuePicture}
                      alt={venue.name}
                      className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        console.error(`Failed to load image: ${venuePicture}`);
                        (e.target as HTMLImageElement).src = venue.type === 'restaurant' 
                          ? 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop'
                          : 'https://images.unsplash.com/photo-1572116469696-31de0f17cc34?q=80&w=2000&auto=format&fit=crop';
                      }}
                    />
                  ) : (
                    <div className="h-full bg-gray-200 flex items-center justify-center">
                      {venue.type === 'restaurant' ? (
                        <ChefHat className="h-12 w-12 text-gray-400" />
                      ) : (
                        <Beer className="h-12 w-12 text-gray-400" />
                      )}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>

                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {venue.name}
                      </h3>
                      <div className="flex items-center space-x-2 text-gray-600">
                        {venue.type === 'restaurant' ? (
                          <>
                            <ChefHat className="h-4 w-4" />
                            <span>{venue.cuisine_type}</span>
                          </>
                        ) : (
                          <>
                            <Beer className="h-4 w-4" />
                            <span>{venue.bar_type}</span>
                          </>
                        )}
                        <span>•</span>
                        <span>{venue.price_range}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 bg-yellow-100 px-2 py-1 rounded-full">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="text-yellow-700 font-medium">
                        {venue.rating || '4.5'} ({venue.reviews_count || '0'})
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{venue.address}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <DollarSign className="h-4 w-4" />
                      <span>{venue.price_range}</span>
                    </div>
                    {venue.distance && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <MapPin className="h-4 w-4" />
                        <span>{t('nearby.distance').replace('{distance}', venue.distance.toFixed(1))}</span>
                      </div>
                    )}
                  </div>

                  {venue.dietary_options && venue.dietary_options.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {venue.dietary_options.map((option: string) => (
                        <span
                          key={option}
                          className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium"
                        >
                          {option}
                        </span>
                      ))}
                    </div>
                  )}

                  {venue.type === 'restaurant' && (
                    <Link
                      to={`/restaurant/${venue.id}`}
                      className="mt-4 w-full bg-orange-500 text-white px-4 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <Building2 className="h-5 w-5" />
                      <span>{t('nearby.viewDetails')}</span>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}

          {filteredVenues.length === 0 && (
            <div className="col-span-3 bg-white rounded-xl shadow-lg p-12 text-center">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-6" />
              <h3 className="text-xl font-medium text-gray-900 mb-4">{t('nearby.noVenues')}</h3>
              <p className="text-gray-600">
                {t('nearby.noVenuesMessage')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Events Section */}
      {activeTab === 'events' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event, index) => {
            const venue = event.restaurant || event.bar;
            const isBlindDate = event.type === 'blind_date';
            const isDrinkingNight = event.type === 'drinking_night';
            const venuePicture = getVenuePicture(venue);

            return (
              <div
                key={`${event.type}-${event.id}`}
                className={`bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
                  animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative h-48 overflow-hidden group">
                  {venuePicture ? (
                    <img
                      src={venuePicture}
                      alt={venue.name}
                      className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        console.error(`Failed to load image: ${venuePicture}`);
                        (e.target as HTMLImageElement).src = isDrinkingNight
                          ? 'https://images.unsplash.com/photo-1572116469696-31de0f17cc34?q=80&w=2000&auto=format&fit=crop'
                          : 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop';
                      }}
                    />
                  ) : (
                    <div className="h-full bg-gray-200 flex items-center justify-center">
                      {isDrinkingNight ? (
                        <Beer className="h-12 w-12 text-gray-400" />
                      ) : (
                        <ChefHat className="h-12 w-12 text-gray-400" />
                      )}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>

                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {venue.name}
                      </h3>
                      <div className="flex items-center space-x-2 text-gray-600">
                        {isDrinkingNight ? (
                          <>
                            <Beer className="h-4 w-4" />
                            <span>{venue.bar_type}</span>
                          </>
                        ) : (
                          <>
                            <ChefHat className="h-4 w-4" />
                            <span>{venue.cuisine_type}</span>
                          </>
                        )}
                        <span>•</span>
                        <span>{event.price_range}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isBlindDate ? 'bg-pink-100 text-pink-700' :
                      isDrinkingNight ? 'bg-purple-100 text-purple-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {isBlindDate ? t('nearby.blindDate') :
                       isDrinkingNight ? t('nearby.drinkingNight') :
                       t('nearby.dinner')}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(event.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{event.time.slice(0, 5)}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{venue.address}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>{event.spots_available} {t('common.spotsAvailable')}</span>
                    </div>
                  </div>

                  <Link
                    to={`/${event.type === 'blind_date' ? 'blind-dates' :
                         event.type === 'drinking_night' ? 'drinking-buddies' :
                         'search'}`}
                    className="w-full bg-orange-500 text-white px-4 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    {isBlindDate ? (
                      <Heart className="h-5 w-5" />
                    ) : isDrinkingNight ? (
                      <Beer className="h-5 w-5" />
                    ) : (
                      <Utensils className="h-5 w-5" />
                    )}
                    <span>{t('nearby.joinEvent')}</span>
                  </Link>
                </div>
              </div>
            );
          })}

          {filteredEvents.length === 0 && (
            <div className="col-span-3 bg-white rounded-xl shadow-lg p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-6" />
              <h3 className="text-xl font-medium text-gray-900 mb-4">{t('nearby.noEvents')}</h3>
              <p className="text-gray-600">
                {t('nearby.noEventsMessage')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* People Section */}
      {activeTab === 'people' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProfiles.map((profile, index) => (
            <div 
              key={profile.id}
              className={`bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 cursor-pointer ${
                animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onClick={() => navigate(`/profile/${profile.id}`)}
            >
              <div className="relative h-48 overflow-hidden">
                <div className="w-full h-full bg-gradient-to-r from-orange-400 to-orange-600" />
                <div className="absolute -bottom-12 left-6">
                  <div className="w-24 h-24 rounded-xl overflow-hidden border-4 border-white bg-white shadow-lg">
                    {profile.profile_picture ? (
                      <img
                        src={profile.profile_picture}
                        alt={profile.full_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <User className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 pt-16">
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{profile.full_name}</h3>
                  {profile.location && (
                    <div className="flex items-center space-x-2 text-gray-600 mt-1">
                      <MapPin className="h-4 w-4" />
                      <span>{profile.location}</span>
                    </div>
                  )}
                </div>

                {profile.languages && profile.languages.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                      <Globe className="h-4 w-4 text-orange-500" />
                      <span>{t('discover.languages')}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.languages.slice(0, 3).map((language: string) => (
                        <span
                          key={language}
                          className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-medium"
                        >
                          {language}
                        </span>
                      ))}
                      {profile.languages.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          +{profile.languages.length - 3} {t('nearby.more')}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {profile.interests && profile.interests.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                      <Coffee className="h-4 w-4 text-orange-500" />
                      <span>{t('discover.interests')}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.interests.slice(0, 3).map((interest: string) => (
                        <span
                          key={interest}
                          className="px-2 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-medium"
                        >
                          {interest}
                        </span>
                      ))}
                      {profile.interests.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          +{profile.interests.length - 3} {t('nearby.more')}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="grid grid-cols-3 gap-2">
                  <FriendRequestButton 
                    userId={profile.id}
                    onSuccess={() => fetchNearbyProfiles(selectedLocation!.lat, selectedLocation!.lng)}
                    onError={(error) => setLocalError(error.message)}
                  />
                  <button 
                    className="flex items-center justify-center space-x-1 bg-blue-500 text-white px-3 py-2 rounded-xl hover:bg-blue-600 transition-all duration-300"
                    onClick={(e) => handleMessageClick(e, profile.id)}
                    disabled={creatingChat}
                  >
                    {creatingChat ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <MessageCircle className="h-4 w-4" />
                        <span>{t('discover.message')}</span>
                      </>
                    )}
                  </button>
                  <DinnerRecommendationButton 
                    userId={profile.id}
                    userName={profile.full_name || undefined}
                  />
                </div>
              </div>
            </div>
          ))}

          {filteredProfiles.length === 0 && selectedLocation && (
            <div className="col-span-3 bg-white rounded-xl shadow-lg p-12 text-center">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-6" />
              <h3 className="text-xl font-medium text-gray-900 mb-4">{t('nearby.noPeople')}</h3>
              <p className="text-gray-600">
                {t('nearby.noPeopleMessage')}
              </p>
            </div>
          )}
        </div>
      )}

      {!selectedLocation && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center transform transition-all duration-500 hover:shadow-xl">
          <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-6" />
          <h3 className="text-xl font-medium text-gray-900 mb-4">{t('overseas.chooseLocation')}</h3>
          <p className="text-gray-600">
            {t('overseas.enterLocation')}
          </p>
        </div>
      )}
    </div>
  );
};

// Function to get the first available picture from a venue
const getVenuePicture = (venue: any) => {
  if (!venue || !venue.pictures) return null;
  
  // Check exterior pictures first
  if (venue.pictures.exterior && venue.pictures.exterior.length > 0) {
    return venue.pictures.exterior[0];
  }
  
  // Then check interior pictures
  if (venue.pictures.interior && venue.pictures.interior.length > 0) {
    return venue.pictures.interior[0];
  }
  
  // Then check gallery pictures
  if (venue.pictures.gallery && venue.pictures.gallery.length > 0) {
    return venue.pictures.gallery[0];
  }
  
  // Finally check menu pictures
  if (venue.pictures.menu && venue.pictures.menu.length > 0) {
    return venue.pictures.menu[0];
  }
  
  return null;
};

export default Nearby;